'use client'

import { Separator } from '@acko/separator'
import { Typography } from '@acko/typography'
import { Fragment } from 'react'
import { Button } from '@acko/button'
import Image from 'next/image'
import { BikeContinueInsuringCard } from './BikeContinueInsuringCard'
import { BikeRegistrationForm, type BikeRegistrationFormProps } from './BikeRegistrationForm'
import styles from './page.module.css'

interface HeroContentClientProps {
  continueInsuringAdditionalEventProperties?: Record<string, unknown>
  continueInsuringJourney?: any
  continueInsuringSegmentEventProps?: object
  logoHref?: string
  logoSrc?: string
  onNewBikeClick: () => Promise<void> | void
  registrationFormProps: BikeRegistrationFormProps
  viewModel: any
}

export const HeroContentClient = ({
  continueInsuringAdditionalEventProperties,
  continueInsuringJourney,
  continueInsuringSegmentEventProps = {},
  logoHref = '/',
  logoSrc,
  onNewBikeClick,
  registrationFormProps,
  viewModel,
}: HeroContentClientProps) => {
  const features = viewModel?.features ?? []
  const heroImage = viewModel?.heroImage
  const newFlowBanner = viewModel?.newFlowBanner ?? {}

  const handleNewBikeKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      void onNewBikeClick()
    }
  }

  return (
    <>
      {logoSrc ? (
        <a href={logoHref} aria-label="ACKO home" className="inline-flex w-fit">
          <img alt="ACKO" className="h-24 w-auto" src={logoSrc} />
        </a>
      ) : null}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between w-full gap-20 lg:gap-32 min-w-0">
        <div className={`${styles.heroCol} flex flex-col gap-16 lg:gap-32 items-center lg:items-start w-full min-w-0`}>
          <div className={`${styles.productImageFrame} relative w-full flex items-center justify-center lg:order-2`}>
            {heroImage?.mobileSrc ? (
              <picture className={styles.heroPicture}>
                <source media="(min-width: 1025px)" srcSet={heroImage?.desktopSrc} />
                <img
                  alt={heroImage?.alt ?? ''}
                  className={`${styles.productImage} lg:absolute lg:bottom-0 lg:left-0 object-contain lg:object-left-bottom`}
                  src={heroImage.mobileSrc}
                />
              </picture>
            ) : null}
          </div>
          <div className="flex flex-col gap-20 items-center lg:items-start w-full lg:order-1">
            <div className="flex flex-col gap-8 items-center lg:items-start w-full text-center lg:text-left">
              <Typography
                scale="5xl"
                emphasis="bold"
                as="h1"
                className="text-[var(--font-heading-lg-size)] leading-[var(--font-heading-lg-line)] lg:text-[var(--font-display-md-size)] lg:leading-[var(--font-display-md-line)] text-balance !text-center lg:!text-left">
                {viewModel?.heading}
              </Typography>
              <Typography scale="base" color="secondary" className="hidden lg:block lg:!text-left">
                {viewModel?.subHeading}
              </Typography>
              <Typography
                scale="base"
                color="secondary"
                className={`${styles.zeroCommissionText} lg:hidden !text-center`}>
                {viewModel?.subHeading}
              </Typography>
            </div>
            <div className="hidden lg:flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-24 w-full min-w-0">
              {features.map((feature, index) => (
                <Fragment key={feature.title || index}>
                  <div className="flex gap-12 items-center min-w-0">
                    <div
                      className={`${styles.shadowIcon} ${styles.surfaceElevated} flex items-center justify-center rounded-full shrink-0 w-48 h-48`}>
                      <Image
                        alt={feature.iconAlt}
                        className="w-24 h-24 object-contain"
                        src={feature.iconSrc}
                        width={24}
                        height={24}
                      />
                    </div>
                    <div className="flex flex-col gap-4 min-w-0">
                      <Typography scale="xl" emphasis="medium" as="h3" color="primary">
                        {feature.title}
                      </Typography>
                      <Typography scale="base" color="secondary">
                        {feature.description}
                      </Typography>
                    </div>
                  </div>
                  {index < features.length - 1 ? (
                    <Separator orientation="vertical" className="h-40 hidden sm:block shrink-0" decorative />
                  ) : null}
                </Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className={`${styles.widgetCol} flex flex-col gap-20 w-full min-w-0`}>
          {continueInsuringJourney ? (
            <BikeContinueInsuringCard
              additionalEventProperties={continueInsuringAdditionalEventProperties}
              item={continueInsuringJourney}
              commonSegmentEventProps={continueInsuringSegmentEventProps}
            />
          ) : null}
          <BikeRegistrationForm {...registrationFormProps} headingClassName="hidden lg:block" />
          <div
            className={`${styles.newBikeCard} group transition-all duration-200 hover:shadow-lg`}
            role="button"
            tabIndex={0}
            onKeyDown={handleNewBikeKeyDown}>
            <div className="flex flex-col gap-8">
              <Typography scale="xl" emphasis="medium" as="h3" color="primary">
                {newFlowBanner?.heading}
              </Typography>
              <Typography scale="base" color="secondary">
                {newFlowBanner?.subHeading}
              </Typography>
            </div>
            <div className="flex items-center justify-between gap-12 w-full">
              <div className="relative w-full">
                <Button
                  onClick={() => {
                    void onNewBikeClick()
                  }}
                  variant="secondary"
                  size="lg"
                  className="w-full">
                  {' '}
                  Get quote{' '}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
